'use client';
import { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useCompetitor } from '../../../apps/hooks/competitor/useCompetitor';
import { useCompetitorJobStatus } from '../../../apps/hooks/competitor/useCompetitorJobStatus';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes/routes';
import { formatDate } from '../../../utils/dateUtils';
import { SuccessAlert } from './components/SuccessAlert';
import { PageHeader } from './components/PageHeader';
import { CommandName } from '../../../infrastructure/BatchClient';
import { useQuery } from '@tanstack/react-query';

export default function MatchStatusPage() {
  const { startAIMatchJob, exportToGoogleSheet } = useCompetitor();
  const {
    matchStatusQuery,
    succeededSKUMatchJobsQuery,
    runningSKUMatchJobsQuery,
    getRunningJobs,
  } = useCompetitorJobStatus();
  const { refetch } = matchStatusQuery;
  const { data: succeededJobs } = succeededSKUMatchJobsQuery;
  const { data: runningJobs } = runningSKUMatchJobsQuery;
  const [isStartingJob, setIsStartingJob] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const hasRunningJob = Boolean(runningJobs && runningJobs.length > 0);

  const runningExportJobsQuery = useQuery({
    queryKey: ['runningExportJobs'],
    queryFn: () => getRunningJobs(CommandName.EXPORT_SKU_MATCHED),
    refetchInterval: 10000,
    staleTime: 5000,
  });

  const { data: runningExportJobs } = runningExportJobsQuery;
  const hasRunningExportJob = Boolean(runningExportJobs && runningExportJobs.length > 0);

  const handleStartAIMatch = async () => {
    setIsStartingJob(true);
    try {
      await startAIMatchJob();
      await refetch();
      await runningSKUMatchJobsQuery.refetch();
    } finally {
      setIsStartingJob(false);
    }
  };

  const handleExportToGoogleSheet = async () => {
    setIsExporting(true);
    try {
      await exportToGoogleSheet();
      await runningExportJobsQuery.refetch();
    } finally {
      setIsExporting(false);
    }
  };

  const handleReviewAIMatched = () => {
    navigate(ROUTES.competitor.matchSKU.default);
  };

  return (
    <Container className="mt-4">
      <PageHeader
        startAIMatchJob={handleStartAIMatch}
        onReviewAIMatched={handleReviewAIMatched}
        exportToGoogleSheet={handleExportToGoogleSheet}
        isStartingJob={isStartingJob}
        isExporting={isExporting}
        hasRunningJob={hasRunningJob}
        hasRunningExportJob={hasRunningExportJob}
      />

      {succeededJobs && succeededJobs.length > 0 && (
        <SuccessAlert succeededJobs={succeededJobs} formatDate={formatDate} />
      )}
    </Container>
  );
}
